/**
 * Core Widget Registration (DRR-321)
 *
 * Registers core dashboard widgets with the widget registry.
 * Import this file early in App.tsx to ensure widgets are registered
 * before dashboard rendering.
 */

import React from 'react';
import { registerWidget, type WidgetProps } from '@ferp/core';
import { FhirClinicalPanel } from '@ferp/subgraph-fhir';
import { Alert } from '@mantine/core';

// Import dashboard panel components
import { DocumentListSection } from '@/pages/subject-dashboard/DocumentListSection';
import { LifecyclePhaseCard } from '@/pages/subject-dashboard/LifecyclePhaseCard';
import { TasksPanel } from '@/pages/subject-dashboard/TasksPanel';
import { EventsPanel } from '@/pages/subject-dashboard/EventsPanel';
import { JourneyPanel } from '@/pages/subject-dashboard/JourneyPanel';
import { CareTeamPanel } from '@/pages/subject-dashboard/CareTeamPanel';

// Import new core widgets
import { ReportLauncherWidget } from './ReportLauncherWidget';
import { FormListWidget } from './FormListWidget';
import { TaskCalendarWidget } from './TaskCalendarWidget';
import { SecureMessagingWidget } from './SecureMessagingWidget';
import { ErpBillingSummaryWidget } from './ErpBillingSummaryWidget';
import { TrustAccountWidget } from './TrustAccountWidget';

/**
 * Wrapper for FhirClinicalPanel that adapts WidgetProps to FhirClinicalPanelProps.
 * FhirClinicalPanel requires tenantNum (number), WidgetProps has tenantNum as optional.
 */
function FhirClinicalPanelWidget({ subjectActorId, tenantNum }: WidgetProps) {
  if (!tenantNum) {
    return React.createElement(Alert, { color: 'gray', variant: 'light' }, 'Clinical data not available.');
  }
  return React.createElement(FhirClinicalPanel, { subjectActorId, tenantNum });
}

// ============================================================================
// Register Core Dashboard Panels (Phase 1 - extracted from SubjectDashboardPage)
// ============================================================================

registerWidget({
  componentHint: 'fhir-clinical-panel',
  component: FhirClinicalPanelWidget,
  package: 'fhir',
  label: 'Clinical Summary',
  category: 'clinical',
});

registerWidget({
  componentHint: 'document-list',
  component: DocumentListSection,
  package: 'core',
  label: 'Documents',
  category: 'operational',
});

registerWidget({
  componentHint: 'lifecycle-phase',
  component: LifecyclePhaseCard,
  package: 'core',
  label: 'Lifecycle Phase',
  category: 'operational',
});

registerWidget({
  componentHint: 'task-list',
  component: TasksPanel,
  package: 'core',
  label: 'Active Tasks',
  category: 'operational',
});

registerWidget({
  componentHint: 'timeline',
  component: EventsPanel,
  package: 'core',
  label: 'Care Events',
  category: 'operational',
});

registerWidget({
  componentHint: 'journey-panel',
  component: JourneyPanel,
  package: 'core',
  label: 'Journey',
  category: 'operational',
});

registerWidget({
  componentHint: 'care-team-panel',
  component: CareTeamPanel,
  package: 'core',
  label: 'Care Team',
  category: 'operational',
});

// ============================================================================
// Register New Core Widgets (Phase 4 - DRR-321)
// ============================================================================

registerWidget({
  componentHint: 'report-launcher',
  component: ReportLauncherWidget,
  package: 'core',
  label: 'Reports',
  category: 'reporting',
});

registerWidget({
  componentHint: 'form-list',
  component: FormListWidget,
  package: 'core',
  label: 'Forms',
  category: 'operational',
});

registerWidget({
  componentHint: 'task-calendar',
  component: TaskCalendarWidget,
  package: 'core',
  label: 'Task Calendar',
  category: 'operational',
});

registerWidget({
  componentHint: 'secure-messaging',
  component: SecureMessagingWidget,
  package: 'core',
  label: 'Secure Messages',
  category: 'communication',
});

// ============================================================================
// Register ERP Widgets
// ============================================================================

registerWidget({
  componentHint: 'erp-billing-summary',
  component: ErpBillingSummaryWidget,
  package: 'erp',
  label: 'Billing Summary',
  category: 'financial',
});

registerWidget({
  componentHint: 'trust-account',
  component: TrustAccountWidget,
  package: 'erp',
  label: 'Trust Account',
  category: 'financial',
});

// Log registration count for debugging
if (import.meta.env.DEV) {
  console.log('[WidgetRegistry] Core widgets registered: 13');
}
